import { AnyAction } from 'redux';
import { AxiosResponse } from 'axios';
import {
	getDeteriorationModelAssetBreakdownChart,
	getDeteriorationModelFailureGradeMatrix,
	updateFailureGradeRange,
	updateFailureMatrixGrade as updateFailureMatrixGradeService,
} from '@Services/deteriorationModels.services';
import { delay, put, retry, takeLatest } from '@redux-saga/core/effects';
import {
	getAssetBreakDownChartAction,
	getAssetBreakDownChartRejected,
	getAssetBreakDownChartResolved,
	getDeteriorationModelStatus,
	getFailureGradeMatrixAction,
	getFailureGradeMatrixRejected,
	getFailureGradeMatrixResolved,
	updateFailureGradeMatrixRanges,
	updateFailureGradeMatrixRangesRejected,
	updateFailureGradeMatrixRangesResolved,
	updateFailureMatrixGrade,
	updateFailureMatrixGradeRejected,
	updateFailureMatrixGradeResolved,
} from '@Actions';
import { AssetBreakDownResponse, GetDeteriorationModelFailureGradeChartResponse } from '@Types';
import { t } from 'i18next';
import { error, success } from '@innovyze/stylovyze';

export function* getFailureGradeMatrixSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelFailureGradeChartResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelFailureGradeMatrix,
			action.payload.modelId,
		);
		const data = payload.data;
		yield put(
			getFailureGradeMatrixResolved({
				failure_grade_config: data.failure_grade_config,
				modelId: action.payload.modelId || '',
				latestRunResultId: action.payload.latestRunResultId || '',
			}),
		);
	} catch (e) {
		console.error('Error retrieving Failure Grade Matrix:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getFailureGradeMatrixRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get Failure Grade Matrix')));
	}
}

export function* updateFailureGradeMatrixRangesSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelFailureGradeChartResponse> = yield retry(
			3,
			10000,
			updateFailureGradeRange,
			action.payload,
		);
		const data = payload.data;
		yield put(
			updateFailureGradeMatrixRangesResolved({
				failure_grade_config: data.failure_grade_config,
				modelId: action.payload.modelId || '',
			}),
		);

		// Refresh model status after updating ranges (backend might start asset recalculation)
		// Wait 1 second to allow backend to process and potentially change model status
		yield delay(1000);
		yield put(getDeteriorationModelStatus({ id: action.payload.modelId }));

		// Show success message if provided by backend
		if (data.message) {
			// Customize/shorten the message if needed
			let displayMessage = data.message;
			if (
				data.message.includes(
					'Successfully updated the failure grade config range, the assets count and asset grades will be recalculated shortly.',
				)
			) {
				displayMessage = t('Ranges updated. Calculating asset counts...');
			}
			yield put(success(displayMessage));
		}
	} catch (e) {
		console.error('Error updating Failure Grade Matrix ranges:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			updateFailureGradeMatrixRangesRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to update Failure Grade Matrix ranges')));
	}
}

export function* updateFailureMatrixGradeSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelFailureGradeChartResponse> = yield retry(
			3,
			2500,
			updateFailureMatrixGradeService,
			action.payload,
		);
		const data = payload.data;
		yield put(
			updateFailureMatrixGradeResolved({
				failure_grade_config: data.failure_grade_config,
				modelId: action.payload.modelId || '',
			}),
		);
	} catch (e) {
		console.error('Error updating Failure Grade Matrix ranges:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			updateFailureMatrixGradeRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to update Failure Grade')));
	}
}

export function* getAssetBreakDownChartSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<AssetBreakDownResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelAssetBreakdownChart,
			action.payload.modelId,
		);
		const data = payload.data;
		yield put(
			getAssetBreakDownChartResolved({
				asset_breakdowns: data.asset_breakdowns,
				modelId: action.payload.modelId || '',
				runResultsId: action.payload.runResultsId,
			}),
		);
	} catch (e) {
		console.error('Error retrieving Asset Breakdown chart:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getAssetBreakDownChartRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get Asset Breakdown chart')));
	}
}

function* watchGetFailureGradeMatrixSaga() {
	yield takeLatest(getFailureGradeMatrixAction, getFailureGradeMatrixSaga);
}

function* watchUpdateFailureGradeMatrixRangesSaga() {
	yield takeLatest(updateFailureGradeMatrixRanges, updateFailureGradeMatrixRangesSaga);
}

function* watchUpdateFailureMatrixGradeSaga() {
	yield takeLatest(updateFailureMatrixGrade, updateFailureMatrixGradeSaga);
}

function* watchGetAssetBreakDownChartSaga() {
	yield takeLatest(getAssetBreakDownChartAction, getAssetBreakDownChartSaga);
}

const sagaArray = [
	watchGetFailureGradeMatrixSaga(),
	watchUpdateFailureGradeMatrixRangesSaga(),
	watchUpdateFailureMatrixGradeSaga(),
	watchGetAssetBreakDownChartSaga(),
];

export default sagaArray;
